import { useCallback, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBoxArchive,
  faArrowRotateLeft,
  faRotateRight,
  faEye,
} from '@fortawesome/free-solid-svg-icons';
import { cn } from '@/lib/cn';
import { toast, swalConfirm } from '@/lib/swal';
import { listAssets, restoreAsset } from './api/ame-platform.api';
import type { AssetRecord } from './api/ame-platform.api';
import { AssetStatusBadge } from './components/AssetStatusBadge';
import { FileSizeDisplay } from './components/FileSizeDisplay';
import { AssetMetadataModal } from './components/AssetMetadataModal';

export function QuarantinePage() {
  const [assets, setAssets] = useState<AssetRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedAsset, setSelectedAsset] = useState<AssetRecord | null>(null);
  const [restoringId, setRestoringId] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      listAssets({ status: 'SOFT_DELETED', limit: 50 }),
      listAssets({ status: 'QUARANTINED', limit: 50 }),
    ])
      .then(([soft, quar]) => setAssets([...soft, ...quar]))
      .catch(() => toast.error('Failed to load quarantine queue'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleRestore = async (asset: AssetRecord) => {
    const confirmed = await swalConfirm({
      title: 'Restore Asset',
      html: `Restore <strong>${asset.originalFileName}</strong> to ACTIVE status?<br/>The asset will be immediately accessible again.`,
      confirmText: 'Restore',
    });
    if (!confirmed) return;
    setRestoringId(asset.id);
    try {
      await restoreAsset(asset.id);
      toast.success(`${asset.originalFileName} restored`);
      load();
    } catch {
      toast.error('Failed to restore asset');
    } finally {
      setRestoringId(null);
    }
  };

  const softDeleted = assets.filter((a) => a.assetStatus === 'SOFT_DELETED');
  const quarantined = assets.filter((a) => a.assetStatus === 'QUARANTINED');

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-xl font-semibold text-foreground">Quarantine Queue</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Soft-deleted and quarantined assets awaiting permanent deletion
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className={cn(
            'flex items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted',
            loading && 'opacity-50 cursor-not-allowed',
          )}
        >
          <FontAwesomeIcon
            icon={faRotateRight}
            className={cn('h-3.5 w-3.5', loading && 'animate-spin')}
          />
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
        <div className="rounded-xl border border-border bg-card p-4">
          <p className="text-xs text-muted-foreground">Soft Deleted</p>
          {loading ? (
            <div className="mt-1 h-7 w-10 animate-pulse rounded bg-muted" />
          ) : (
            <p className="mt-0.5 text-2xl font-bold text-foreground">{softDeleted.length}</p>
          )}
        </div>
        <div className="rounded-xl border border-border bg-card p-4">
          <p className="text-xs text-muted-foreground">Quarantined</p>
          {loading ? (
            <div className="mt-1 h-7 w-10 animate-pulse rounded bg-muted" />
          ) : (
            <p className="mt-0.5 text-2xl font-bold text-foreground">{quarantined.length}</p>
          )}
        </div>
        <div className="rounded-xl border border-border bg-card p-4">
          <p className="text-xs text-muted-foreground">Total in Queue</p>
          {loading ? (
            <div className="mt-1 h-7 w-10 animate-pulse rounded bg-muted" />
          ) : (
            <p className="mt-0.5 text-2xl font-bold text-foreground">{assets.length}</p>
          )}
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card p-4">
        <div className="flex items-start gap-3">
          <FontAwesomeIcon
            icon={faBoxArchive}
            className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground"
          />
          <p className="text-xs text-muted-foreground">
            Soft-deleted assets enter quarantine after the configured grace period (typically 7
            days). Quarantined assets are permanently purged after the retention period (typically
            30 days). Permanent deletion and purge scheduling are handled by the AME lifecycle job —
            not available via UI.
          </p>
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border border-border bg-card">
        <table className="min-w-full divide-y divide-border text-sm">
          <thead>
            <tr className="bg-muted/40">
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground">
                File Name
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground">
                Module
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground">
                Class
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground">
                Size
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground">
                Status
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground">
                Created
              </th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-muted-foreground">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading ? (
              Array.from({ length: 4 }).map((_, i) => (
                <tr key={i}>
                  {Array.from({ length: 7 }).map((__, j) => (
                    <td key={j} className="px-4 py-3">
                      <div className="h-4 animate-pulse rounded bg-muted" />
                    </td>
                  ))}
                </tr>
              ))
            ) : assets.length === 0 ? (
              <tr>
                <td colSpan={7} className="py-16 text-center">
                  <FontAwesomeIcon
                    icon={faBoxArchive}
                    className="h-8 w-8 text-muted-foreground mb-3"
                  />
                  <p className="text-sm font-medium text-foreground">Quarantine queue is empty</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    No soft-deleted or quarantined assets
                  </p>
                </td>
              </tr>
            ) : (
              assets.map((asset) => (
                <tr key={asset.id} className="hover:bg-muted/30 transition-colors">
                  <td className="max-w-[180px] truncate px-4 py-3 text-sm font-medium text-foreground">
                    {asset.originalFileName}
                  </td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">{asset.moduleId}</td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">{asset.assetClass}</td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">
                    <FileSizeDisplay bytes={asset.fileSizeBytes} />
                  </td>
                  <td className="px-4 py-3">
                    <AssetStatusBadge status={asset.assetStatus} />
                  </td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">
                    {new Date(asset.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => setSelectedAsset(asset)}
                        className="flex h-7 w-7 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                        title="View details"
                      >
                        <FontAwesomeIcon icon={faEye} className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => void handleRestore(asset)}
                        disabled={restoringId === asset.id}
                        className={cn(
                          'flex h-7 w-7 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-green-500/10 hover:text-green-600',
                          restoringId === asset.id && 'opacity-50 cursor-not-allowed',
                        )}
                        title="Restore asset"
                      >
                        <FontAwesomeIcon icon={faArrowRotateLeft} className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {selectedAsset && (
        <AssetMetadataModal asset={selectedAsset} onClose={() => setSelectedAsset(null)} />
      )}
    </div>
  );
}
